import { AlertTriangle, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";
import "./runtime-settings.css";

interface RuntimeSettings {
  channelConfigured: boolean;
  liveOnlyEnforced: true;
  chatRetentionDays: number;
  moderatorCommandsEnabled: boolean;
  keepModerationIncidents: boolean;
}

type MutableSetting = "moderatorCommandsEnabled" | "keepModerationIncidents";

function settingRow(title: string): HTMLElement | null {
  for (const row of document.querySelectorAll<HTMLElement>(".settings-grid .setting-row")) {
    if (row.querySelector("strong")?.textContent?.trim() === title) return row;
  }
  return null;
}

function setRowState(title: string, checked: boolean, disabled: boolean, detail?: string): void {
  const row = settingRow(title);
  if (!row) return;
  const toggle = row.querySelector<HTMLButtonElement>(".toggle");
  if (toggle) {
    toggle.classList.toggle("on", checked);
    toggle.setAttribute("aria-checked", String(checked));
    toggle.disabled = disabled;
  }
  if (detail) {
    const paragraph = row.querySelector("p");
    if (paragraph) paragraph.textContent = detail;
  }
}

function applySettings(settings: RuntimeSettings, busy: boolean): void {
  setRowState(
    "Run only while channel is live",
    settings.liveOnlyEnforced,
    true,
    "Enforced safety policy. Normal bot activity stays live-only; offline command test mode remains isolated.",
  );
  setRowState(
    "Allow approved Kick moderator commands",
    settings.moderatorCommandsEnabled,
    busy || !settings.channelConfigured,
    settings.channelConfigured
      ? "Controls moderator-only custom commands and the approved !timeout command. Dashboard access remains owner-only."
      : "Connect Kick before changing moderator command access.",
  );
  setRowState(
    "90-day chat retention",
    true,
    true,
    `Server retention policy. Full chat messages are automatically removed after ${settings.chatRetentionDays} days.`,
  );
  setRowState(
    "Keep moderation incidents",
    settings.keepModerationIncidents,
    busy || !settings.channelConfigured,
    settings.channelConfigured
      ? settings.keepModerationIncidents
        ? "Moderation incidents are kept until you delete them or honor an erasure request."
        : `Moderation incidents older than ${settings.chatRetentionDays} days are automatically removed.`
      : "Connect Kick before changing moderation incident retention.",
  );
}

function mutableSettingForRow(row: HTMLElement): MutableSetting | null {
  const title = row.querySelector("strong")?.textContent?.trim();
  if (title === "Allow approved Kick moderator commands") return "moderatorCommandsEnabled";
  if (title === "Keep moderation incidents") return "keepModerationIncidents";
  return null;
}

export function RuntimeSettingsController() {
  const [settings, setSettings] = useState<RuntimeSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const loaded = useRef(false);

  const refresh = useCallback(async (force = false) => {
    if (loaded.current && !force) return;
    loaded.current = true;
    try {
      const next = await api<RuntimeSettings>("/api/settings/runtime");
      setSettings(next);
      applySettings(next, false);
    } catch (caught) {
      loaded.current = false;
      setError(caught instanceof Error ? caught.message : "Could not load runtime settings.");
    }
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (!settingRow("Run only while channel is live")) return;
      if (settings) applySettings(settings, busy);
      else void refresh();
    });
    observer.observe(document.getElementById("root") ?? document.body, { childList: true, subtree: true });
    if (settingRow("Run only while channel is live")) void refresh();
    return () => observer.disconnect();
  }, [busy, refresh, settings]);

  useEffect(() => {
    async function intercept(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const toggle = target.closest<HTMLButtonElement>(".settings-grid .toggle");
      const row = toggle?.closest<HTMLElement>(".setting-row");
      if (!toggle || !row) return;
      const key = mutableSettingForRow(row);
      if (!key || !settings || toggle.disabled) return;

      event.preventDefault();
      event.stopPropagation();
      setBusy(true);
      setError("");
      setNotice("");
      applySettings(settings, true);
      try {
        const next = await api<RuntimeSettings>("/api/settings/runtime", {
          method: "PATCH",
          body: JSON.stringify({ [key]: !settings[key] }),
        });
        setSettings(next);
        applySettings(next, false);
        setNotice(key === "moderatorCommandsEnabled" ? "Moderator command access updated." : "Moderation incident retention updated.");
      } catch (caught) {
        applySettings(settings, false);
        setError(caught instanceof Error ? caught.message : "Could not save this setting.");
      } finally {
        setBusy(false);
      }
    }
    document.addEventListener("click", intercept, true);
    return () => document.removeEventListener("click", intercept, true);
  }, [settings]);

  useEffect(() => {
    if (!settings) return;
    applySettings(settings, busy);
  }, [busy, settings]);

  if (!error && !notice) return null;
  return <div className={`runtime-settings-toast ${error ? "error" : "success"}`} role="status">
    {error ? <AlertTriangle size={16} /> : <ShieldCheck size={16} />}
    <span>{error || notice}</span>
    <button type="button" onClick={() => { setError(""); setNotice(""); }}>×</button>
  </div>;
}
